import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { LIST_PROMO_CODES, CREATE_PROMO_CODE, DEACTIVATE_PROMO_CODE } from '../graphql/admin';
import { AdminLayout } from '../components/AdminLayout';

export function AdminPromosPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    code: '',
    plan: 'pro',
    discountType: '100_percent',
    discountAmount: 0,
    maxUses: 1,
    expiresAt: '',
    allowedEmails: '',
    allowedDomains: '',
  });

  const { data, loading, refetch } = useQuery(LIST_PROMO_CODES, {
    variables: { includeInactive: true },
  });

  const [createPromo, { loading: creating }] = useMutation(CREATE_PROMO_CODE, {
    onCompleted: () => {
      setShowCreateForm(false);
      setFormData({
        code: '',
        plan: 'pro',
        discountType: '100_percent',
        discountAmount: 0,
        maxUses: 1,
        expiresAt: '',
        allowedEmails: '',
        allowedDomains: '',
      });
      refetch();
    },
  });

  const [deactivatePromo] = useMutation(DEACTIVATE_PROMO_CODE, {
    onCompleted: () => refetch(),
  });

  const promos = data?.adminListPromoCodes || [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const input: any = {
      code: formData.code.trim().toUpperCase(),
      plan: formData.plan,
      discountType: formData.discountType,
      maxUses: formData.maxUses,
    };

    if (formData.discountType !== '100_percent') {
      input.discountAmount = formData.discountAmount;
    }

    if (formData.expiresAt) {
      input.expiresAt = new Date(formData.expiresAt).toISOString();
    }

    if (formData.allowedEmails) {
      input.allowedEmails = formData.allowedEmails.split(',').map((e) => e.trim().toLowerCase());
    }

    if (formData.allowedDomains) {
      input.allowedDomains = formData.allowedDomains.split(',').map((d) => d.trim().toLowerCase());
    }

    await createPromo({ variables: { input } });
  };

  const handleDeactivate = async (id: number) => {
    if (window.confirm('Are you sure you want to deactivate this promo code?')) {
      await deactivatePromo({ variables: { promoCodeId: id } });
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDiscountDisplay = (promo: any) => {
    if (promo.discountType === '100_percent') {
      return '100% Free';
    } else if (promo.discountType === 'percentage') {
      return `${promo.discountAmount}% Off`;
    } else {
      return `$${(promo.discountAmount / 100).toFixed(2)} Off`;
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Promo Code Management</h1>
            <p className="text-gray-600 mt-1">
              Total codes: {promos.length} ({promos.filter((p: any) => p.isActive).length} active)
            </p>
          </div>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {showCreateForm ? 'Cancel' : '+ Create Promo Code'}
          </button>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Promo Code</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Promo Code *
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                    placeholder="SUMMER2025"
                    required
                    maxLength={50}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Letters, numbers, and underscores only</p>
                </div>

                {/* Plan */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan *</label>
                  <select
                    value={formData.plan}
                    onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="pro">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>

                {/* Discount Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Type *
                  </label>
                  <select
                    value={formData.discountType}
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="100_percent">100% Free</option>
                    <option value="percentage">Percentage Off</option>
                    <option value="fixed_amount">Fixed Amount Off</option>
                  </select>
                </div>

                {/* Discount Amount */}
                {formData.discountType !== '100_percent' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {formData.discountType === 'percentage' ? 'Percentage' : 'Amount (cents)'}
                    </label>
                    <input
                      type="number"
                      value={formData.discountAmount}
                      onChange={(e) =>
                        setFormData({ ...formData, discountAmount: parseInt(e.target.value) || 0 })
                      }
                      min="0"
                      max={formData.discountType === 'percentage' ? '100' : undefined}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* Max Uses */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Max Uses *
                  </label>
                  <input
                    type="number"
                    value={formData.maxUses}
                    onChange={(e) =>
                      setFormData({ ...formData, maxUses: parseInt(e.target.value) || 1 })
                    }
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Expires At */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expires At (optional)
                  </label>
                  <input
                    type="date"
                    value={formData.expiresAt}
                    onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Allowed Emails */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allowed Emails (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.allowedEmails}
                    onChange={(e) => setFormData({ ...formData, allowedEmails: e.target.value })}
                    placeholder="user1@example.com, user2@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Comma-separated email addresses</p>
                </div>

                {/* Allowed Domains */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allowed Domains (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.allowedDomains}
                    onChange={(e) => setFormData({ ...formData, allowedDomains: e.target.value })}
                    placeholder="company.com, startup.io"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Comma-separated domain names</p>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Promo Code'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Promo Codes List */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-pulse">Loading promo codes...</div>
            </div>
          ) : promos.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No promo codes created yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Code</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Plan</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Discount
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Usage
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Expires
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Restrictions
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {promos.map((promo: any) => (
                    <tr key={promo.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <div className="font-mono font-semibold text-gray-900">{promo.code}</div>
                        <div className="text-xs text-gray-500">ID: {promo.id}</div>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            promo.plan === 'enterprise'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}
                        >
                          {promo.plan.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">
                          {getDiscountDisplay(promo)}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-sm">
                          <span
                            className={
                              promo.currentUses >= promo.maxUses
                                ? 'text-red-600 font-semibold'
                                : 'text-gray-900'
                            }
                          >
                            {promo.currentUses}
                          </span>
                          <span className="text-gray-600"> / {promo.maxUses}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {promo.expiresAt ? (
                          <span
                            className={
                              new Date(promo.expiresAt) < new Date()
                                ? 'text-red-600'
                                : 'text-gray-600'
                            }
                          >
                            {formatDate(promo.expiresAt)}
                          </span>
                        ) : (
                          'Never'
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-xs text-gray-600">
                          {promo.allowedEmails?.length > 0 && (
                            <div>Emails: {promo.allowedEmails.length}</div>
                          )}
                          {promo.allowedEmailDomains?.length > 0 && (
                            <div>Domains: {promo.allowedEmailDomains.length}</div>
                          )}
                          {!promo.allowedEmails?.length && !promo.allowedEmailDomains?.length && (
                            <div>None</div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            promo.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {promo.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {promo.isActive && (
                          <button
                            onClick={() => handleDeactivate(promo.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
